import React, { useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { db, auth } from "../firebase";
import {
  collection,
  doc,
  setDoc,
  onSnapshot,
  query,
  where,
  deleteDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  getDoc,
} from "firebase/firestore";

import { motion, AnimatePresence } from "framer-motion";
import { containsBadWord } from "../utils/badWords";
import { toast } from "sonner";

function StudyBuddiesPage({ setCurrentRoom }) {
  
  const [isWorking, setIsWorking] = useState(false);
  const [workingUsers, setWorkingUsers] = useState([]);
  const [roomName, setRoomName] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [publicRooms, setPublicRooms] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("working");
  const [isAdmin, setIsAdmin] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [roomSearchQuery, setRoomSearchQuery] = useState("");
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [visibleRooms, setVisibleRooms] = useState(5);
  const [visibleUsers, setVisibleUsers] = useState(5);
  const navigate = useNavigate();
  const currentUser = auth.currentUser;

  useEffect(() => {
    if (!currentUser) {
      navigate("/auth");
      return;
    }

    

    // Admin kontrolü
    const fetchAdminStatus = async () => {
      try {
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists() && userSnap.data().role === "admin") {
          setIsAdmin(true);
        }
      } catch (err) {
        setError(`Admin durumu kontrol edilirken hata: ${err.message}`);
        toast.error(`Admin durumu kontrol edilirken hata: ${err.message}`);
      }
    };
    fetchAdminStatus();

    const workingQuery = query(
      collection(db, "workingUsers"),
      where("active", "==", true)
    );
    const unsubscribeWorking = onSnapshot(
      workingQuery,
      (snapshot) => {
        const users = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setWorkingUsers(users);
        setIsWorking(users.some((user) => user.id === currentUser.uid));
      },
      (err) => {
        setError(`Çalışan kullanıcılar yüklenirken hata: ${err.message}`);
        toast.error(`Çalışan kullanıcılar yüklenirken hata: ${err.message}`);
      }
    );

    const publicRoomsQuery = query(
      collection(db, "focusRooms"),
      where("isPublic", "==", true)
    );
    const unsubscribeRooms = onSnapshot(
      publicRoomsQuery,
      (snapshot) => {
        const rooms = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPublicRooms(rooms);
      },
      (err) => {
        setError(`Genel odalar yüklenirken hata: ${err.message}`);
        toast.error(`Genel odalar yüklenirken hata: ${err.message}`);
      }
    );

    const invitationsQuery = query(
      collection(db, "focusRooms"),
      where("invitedUsers", "array-contains", currentUser.uid)
    );
    const unsubscribeInvitations = onSnapshot(
      invitationsQuery,
      (snapshot) => {
        const invites = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setInvitations(
          invites.filter((invite) => !invite.participants.includes(currentUser.uid))
        );
      },
      (err) => {
        setError(`Davetler yüklenirken hata: ${err.message}`);
        toast.error(`Davetler yüklenirken hata: ${err.message}`);
      }
    );

    return () => {
      unsubscribeWorking();
      unsubscribeRooms();
      unsubscribeInvitations();
    };
  }, [currentUser, navigate]);

  

  const handleWorkingToggle = async () => {
    if (!currentUser) return;
    const userRef = doc(db, "workingUsers", currentUser.uid);
    try {
      if (isWorking) {
        await deleteDoc(userRef);
        setIsWorking(false);
        toast.success("Çalışma durumu kapatıldı.");
      } else {
        await setDoc(userRef, {
          username: localStorage.getItem("username") || "Bilinmeyen",
          active: true,
          timestamp: new Date().toISOString(),
        });
        setIsWorking(true);
        toast.success("Şu an çalışıyorum durumu açıldı!");
      }
    } catch (err) {
      setError(`Çalışma durumu değiştirilirken hata: ${err.message}`);
      toast.error(`Çalışma durumu değiştirilirken hata: ${err.message}`);
    }
  };

  const handleCreateRoom = async () => {
    if (!roomName || !currentUser) {
      toast.error("Oda adı boş olamaz!");
      return;
    }

    if (containsBadWord(roomName.toLowerCase())) {
      setError("Oda adı uygun olmayan kelimeler içeriyor.");
      toast.error("Oda adı uygun olmayan kelimeler içeriyor.");
      return;
    }

    try {
      const roomId = `${currentUser.uid}-${Date.now()}`;
      const roomRef = doc(db, "focusRooms", roomId);
      await setDoc(roomRef, {
        name: roomName,
        owner: currentUser.uid,
        ownerUsername: localStorage.getItem("username") || "Bilinmeyen",
        participants: [currentUser.uid],
        invitedUsers: [],
        isRunning: false,
        elapsed: 0,
        mode: "timer",
        duration: 0,
        isPublic,
        timestamp: new Date().toISOString(),
      });

      setCurrentRoom(roomId);
      navigate(`/focus-room/${roomId}`);
      toast.success("Oda başarıyla oluşturuldu!");
      setShowCreateModal(false);
      setRoomName("");
      setIsPublic(false);
    } catch (err) {
      setError(`Oda oluşturulurken hata: ${err.message}`);
      toast.error(`Oda oluşturulurken hata: ${err.message}`);
    }
  };

  const handleJoinRoom = (roomId) => {
    setCurrentRoom(roomId);
    navigate(`/focus-room/${roomId}`);
    toast.success("Odaya katıldınız!");
  };

  const handleAcceptInvite = async (roomId) => {
    try {
      const roomRef = doc(db, "focusRooms", roomId);
      await updateDoc(roomRef, {
        participants: arrayUnion(currentUser.uid),
        invitedUsers: arrayRemove(currentUser.uid),
      });
      setCurrentRoom(roomId);
      navigate(`/focus-room/${roomId}`);
      toast.success("Davet kabul edildi!");
    } catch (err) {
      setError(`Davet kabul edilirken hata: ${err.message}`);
      toast.error(`Davet kabul edilirken hata: ${err.message}`);
    }
  };

  const handleRejectInvite = async (roomId) => {
    try {
      const roomRef = doc(db, "focusRooms", roomId);
      await updateDoc(roomRef, {
        invitedUsers: arrayRemove(currentUser.uid),
      });
      toast.success("Davet reddedildi.");
    } catch (err) {
      setError(`Davet reddedilirken hata: ${err.message}`);
      toast.error(`Davet reddedilirken hata: ${err.message}`);
    }
  };

  const handleDeleteRoom = async (roomId, roomOwner) => {
    if (!currentUser) return;

    if (currentUser.uid !== roomOwner && !isAdmin) {
      setError("Bu odayı silme yetkiniz yok.");
      toast.error("Bu odayı silme yetkiniz yok.");
      return;
    }

    try {
      const roomRef = doc(db, "focusRooms", roomId);
      await deleteDoc(roomRef);
      toast.success("Oda başarıyla silindi!");
    } catch (err) {
      setError(`Oda silinirken hata: ${err.message}`);
      toast.error(`Oda silinirken hata: ${err.message}`);
    }
  };

  // Oda ve davet filtreleme
  const filteredRooms = publicRooms.filter(
    (room) =>
      (room.name || "").toLowerCase().includes(roomSearchQuery.toLowerCase()) ||
      (room.ownerUsername || "").toLowerCase().includes(roomSearchQuery.toLowerCase())
  );

  const filteredInvitations = invitations.filter(
    (invite) =>
      (invite.name || "").toLowerCase().includes(roomSearchQuery.toLowerCase()) ||
      (invite.ownerUsername || "").toLowerCase().includes(roomSearchQuery.toLowerCase())
  );

  // Kullanıcı filtreleme
  const filteredUsers = workingUsers.filter((user) =>
    (user.username || "").toLowerCase().includes(userSearchQuery.toLowerCase())
  );

  return (
    <motion.div
      style={containerStyle}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.h2
        style={headerStyle}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.5 }}
      >
        👥 Etüt Arkadaşları
      </motion.h2>

      {/* Sekmeler */}
      <motion.div
        style={tabContainerStyle}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        <motion.button
          style={tabButtonStyle(activeTab === "working")}
          onClick={() => setActiveTab("working")}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Çalışanlar
        </motion.button>
        <motion.button
          style={tabButtonStyle(activeTab === "rooms")}
          onClick={() => setActiveTab("rooms")}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Odalar ve Davetler
        </motion.button>
      </motion.div>

      {/* Sekme İçerikleri */}
      <AnimatePresence mode="wait">
        {activeTab === "working" && (
          <motion.div
            key="working"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <motion.button
              style={buttonStyle(isWorking)}
              onClick={handleWorkingToggle}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {isWorking ? "Çalışmayı Bırak" : "Şu An Çalışıyorum"}
            </motion.button>

            <motion.input
              type="text"
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              placeholder="Kullanıcı ara..."
              style={searchInputStyle}
              whileFocus={{ borderColor: "#00e676" }}
            />

            <motion.h3 style={sectionHeaderStyle}>Şu An Çalışanlar</motion.h3>
            <ul style={listStyle}>
              <AnimatePresence>
                {filteredUsers.slice(0, visibleUsers).map((user, index) => (
                  <motion.li
                    key={user.id}
                    style={listItemStyle}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.1, duration: 0.3 }}
                  >
                    <a href={`/user/${user.id}`} style={usernameLinkStyle}>
                      {user.username}
                    </a>
                  </motion.li>
                ))}
              </AnimatePresence>
            </ul>
            {filteredUsers.length > visibleUsers && (
              <motion.button
                style={loadMoreButtonStyle}
                onClick={() => setVisibleUsers((prev) => prev + 5)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Daha Fazla Göster
              </motion.button>
            )}
          </motion.div>
        )}

        {activeTab === "rooms" && (
          <motion.div
            key="rooms"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <div style={roomHeaderStyle}>
              <motion.input
                type="text"
                value={roomSearchQuery}
                onChange={(e) => setRoomSearchQuery(e.target.value)}
                placeholder="Oda veya kullanıcı ara..."
                style={searchInputStyle}
                whileFocus={{ borderColor: "#00e676" }}
              />
              <motion.button
                style={buttonStyle(false)}
                onClick={() => setShowCreateModal(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Yeni Oda Oluştur
              </motion.button>
            </div>

            <motion.h3 style={sectionHeaderStyle}>Genel Odalar</motion.h3>
            <ul style={listStyle}>
              <AnimatePresence>
                {filteredRooms.slice(0, visibleRooms).map((room, index) => (
                  <motion.li
                    key={room.id}
                    style={listItemStyle}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.1, duration: 0.3 }}
                  >
                    <span>
                      {room.name} - <a href={`/user/${room.owner}`} style={usernameLinkStyle}>{room.ownerUsername}</a>
                    </span>
                    <div style={buttonGroupStyle}>
                      <motion.button
                        style={buttonStyle(false)}
                        onClick={() => handleJoinRoom(room.id)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Katıl
                      </motion.button>
                      {(currentUser.uid === room.owner || isAdmin) && (
                        <motion.button
                          style={deleteButtonStyle}
                          onClick={() => handleDeleteRoom(room.id, room.owner)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          Sil
                        </motion.button>
                      )}
                    </div>
                  </motion.li>
                ))}
              </AnimatePresence>
            </ul>

            <motion.h3 style={sectionHeaderStyle}>Gelen Davetler</motion.h3>
            <ul style={listStyle}>
              <AnimatePresence>
                {filteredInvitations.length > 0 ? (
                  filteredInvitations.slice(0, visibleRooms).map((invite, index) => (
                    <motion.li
                      key={invite.id}
                      style={listItemStyle}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: index * 0.1, duration: 0.3 }}
                    >
                      <span>
                        {invite.name} - <a href={`/user/${invite.owner}`} style={usernameLinkStyle}>{invite.ownerUsername}</a>
                      </span>
                      <div style={buttonGroupStyle}>
                        <motion.button
                          style={buttonStyle(false)}
                          onClick={() => handleAcceptInvite(invite.id)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          Kabul Et
                        </motion.button>
                        <motion.button
                          style={rejectButtonStyle}
                          onClick={() => handleRejectInvite(invite.id)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          Reddet
                        </motion.button>
                        {(currentUser.uid === invite.owner || isAdmin) && (
                          <motion.button
                            style={deleteButtonStyle}
                            onClick={() => handleDeleteRoom(invite.id, invite.owner)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            Sil
                          </motion.button>
                        )}
                      </div>
                    </motion.li>
                  ))
                ) : (
                  <motion.li
                    style={listItemStyle}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.3 }}
                  >
                    Henüz davet yok.
                  </motion.li>
                )}
              </AnimatePresence>
            </ul>

            {(filteredRooms.length > visibleRooms || filteredInvitations.length > visibleRooms) && (
              <motion.button
                style={loadMoreButtonStyle}
                onClick={() => setVisibleRooms((prev) => prev + 5)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Daha Fazla Göster
              </motion.button>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Oda Oluştur Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            style={modalOverlayStyle}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              style={modalContentStyle}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <motion.h3 style={sectionHeaderStyle}>Yeni Oda Oluştur</motion.h3>
              <motion.input
                type="text"
                value={roomName}
                onChange={(e) => setRoomName(e.target.value)}
                placeholder="Oda adını girin"
                style={inputStyle}
                whileFocus={{ borderColor: "#00e676" }}
              />
              <label style={checkboxLabelStyle}>
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  style={checkboxStyle}
                />
                Genel Oda
              </label>
              <div style={buttonGroupStyle}>
                <motion.button
                  style={buttonStyle(false)}
                  onClick={handleCreateRoom}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Oda Oluştur
                </motion.button>
                <motion.button
                  style={rejectButtonStyle}
                  onClick={() => setShowCreateModal(false)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  İptal
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {error && (
          <motion.p
            style={errorTextStyle}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
          >
            {error}
          </motion.p>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// Stiller
const containerStyle = {
  padding: "20px",
  background: "linear-gradient(135deg, #0f2027, #203a43, #2c5364)",
  minHeight: "100vh",
  color: "#f1f1f1",
  fontFamily: "'Inter', sans-serif",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

const headerStyle = {
  fontSize: "28px",
  textAlign: "center",
  marginBottom: "20px",
  color: "#00ffcc",
  textShadow: "0 0 8px rgba(0, 230, 118, 0.2)",
};

const tabContainerStyle = {
  display: "flex",
  justifyContent: "center",
  gap: "10px",
  marginBottom: "20px",
  flexWrap: "wrap",
};

const tabButtonStyle = (isActive) => ({
  padding: "10px 20px",
  background: isActive
    ? "linear-gradient(135deg, #00e676, #00c853)"
    : "rgba(255, 255, 255, 0.1)",
  color: isActive ? "#fff" : "#b0bec5",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  fontSize: "16px",
  fontWeight: "600",
  boxShadow: isActive ? "0 2px 8px rgba(0, 230, 118, 0.2)" : "none",
  transition: "all 0.3s ease",
});

const sectionHeaderStyle = {
  fontSize: "20px",
  margin: "20px 0 10px",
  color: "#00ffcc",
  textShadow: "0 0 6px rgba(0, 230, 118, 0.2)",
};

const buttonStyle = (isWorking) => ({
  padding: "8px 16px",
  background: isWorking
    ? "linear-gradient(135deg, #ff416c, #ff4b2b)"
    : "linear-gradient(135deg, #00e676, #00c853)",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "600",
  margin: "5px",
  minWidth: "100px",
});

const rejectButtonStyle = {
  padding: "8px 16px",
  background: "linear-gradient(135deg, #ff416c, #ff4b2b)",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "600",
  margin: "5px",
  minWidth: "100px",
};

const deleteButtonStyle = {
  padding: "8px 16px",
  background: "linear-gradient(135deg, #ff5252, #d32f2f)",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "600",
  margin: "5px",
  minWidth: "80px",
};

const buttonGroupStyle = {
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
};

const listStyle = {
  listStyleType: "none",
  padding: 0,
  marginBottom: "20px",
};

const listItemStyle = {
  padding: "12px",
  background: "rgba(255, 255, 255, 0.05)",
  borderRadius: "8px",
  marginBottom: "8px",
  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.3)",
  border: "1px solid rgba(0, 230, 118, 0.1)",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "10px",
};

const roomHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "10px",
  flexWrap: "wrap",
  marginBottom: "20px",
};

const searchInputStyle = {
  padding: "10px",
  width: "100%",
  maxWidth: "300px",
  borderRadius: "6px",
  border: "1px solid #2a2a2a",
  background: "rgba(255, 255, 255, 0.15)",
  color: "#f0f0f0",
  fontSize: "14px",
  outline: "none",
};

const loadMoreButtonStyle = {
  padding: "10px 20px",
  background: "linear-gradient(135deg, #00e676, #00c853)",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
  fontSize: "14px",
  fontWeight: "600",
  margin: "10px auto",
  display: "block",
};

const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(0, 0, 0, 0.7)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1000,
};

const modalContentStyle = {
  background: "rgba(30, 40, 50, 0.95)",
  padding: "20px",
  borderRadius: "12px",
  boxShadow: "0 6px 16px rgba(0, 0, 0, 0.4)",
  border: "1px solid rgba(0, 230, 118, 0.3)",
  width: "90%",
  maxWidth: "400px",
  display: "flex",
  flexDirection: "column",
  gap: "15px",
};

const inputStyle = {
  padding: "10px",
  borderRadius: "6px",
  border: "1px solid #2a2a2a",
  background: "rgba(255, 255, 255, 0.15)",
  color: "#f0f0f0",
  fontSize: "14px",
  outline: "none",
};

const checkboxLabelStyle = {
  color: "#b0bec5",
  display: "flex",
  alignItems: "center",
  gap: "5px",
};

const checkboxStyle = {
  transform: "scale(1.2)",
  accentColor: "#00e676",
};

const errorTextStyle = {
  marginTop: "15px",
  textAlign: "center",
  fontSize: "14px",
  color: "#f44336",
};

const usernameLinkStyle = {
  color: "#00ffcc",
  textDecoration: "none",
  fontWeight: "600",
};

export default StudyBuddiesPage;